import { serializeQuery } from "@/utils/UrlUtils";
import { apiFetch, ApiBaseUrl } from "./fetch";

export default class ApiPaths {
  static createPath(appId, path) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths`, {
      method: "POST",
      body: path,
    });
  }

  static getPrompts(appId, params = {}, { signal, statusKey } = {}) {
    const query = {
      metric_periods: [{ period: "last_seven_days" }],
      ...params,
      ...(statusKey && { status_key: statusKey }),
    };
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths?${serializeQuery(query)}`, { signal });
  }

  static getRetentions(appId, params) {
    let query = serializeQuery(params);
    if (query) query = `&${query}`;
    return apiFetch(
      `${ApiBaseUrl}apps/${appId}/paths?path_type=retention_modal,video,horizontal,vertical,tile,text,invisible,widget,interstitial,email,bottom_banner${query}`,
    );
  }

  static getPath(appId, pathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}`);
  }

  static updatePath(appId, pathId, path) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}`, {
      method: "PUT",
      body: path,
    });
  }

  static updatePathState(appId, pathId, stateData) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/update_state`, {
      method: "PUT",
      body: { path: stateData },
    });
  }

  static updatePathSegment(appId, pathId, segments) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/update_segment`, {
      method: "PUT",
      body: { path: { segments } },
    });
  }

  static updatePathTrigger(appId, pathId, triggers) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/update_trigger`, {
      method: "PUT",
      body: { path: { triggers } },
    });
  }

  static updatePathGoal(appId, pathId, goalData) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/update_goal`, {
      method: "PUT",
      body: { path: goalData },
    });
  }

  static uploadBgImageAction(file, action, appId, pathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/upload_background_image_action`, {
      method: "PUT",
      body: {
        image: {
          action_name: action,
          image_file: file,
        },
      },
    });
  }

  static deletePath(appId, pathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}`, {
      method: "DELETE",
    }).then(_ => pathId);
  }

  static getActivities(appId, pathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/activity`);
  }

  static getVideoUploadUrl(appId, pathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/video_upload_url`);
  }

  static setPriorityRetentions(appId, pathGroups) {
    const prioritizedList = pathGroups.reduce((h, pathGroup) => {
      return {
        ...h,
        [pathGroup.id]: { priority: pathGroup.priority },
      };
    }, {});
    return apiFetch(
      `${ApiBaseUrl}apps/${appId}/paths/set_priority?path_type=retention_modal,video,horizontal,vertical,tile,text,invisible,widget,interstitial`,
      {
        method: "POST",
        body: { path_groups: prioritizedList },
      },
    );
  }

  static setOrderRetentions(appId, paths) {
    const prioritizedList = paths.reduce((h, path) => {
      return {
        ...h,
        [path.id]: { order: path.order },
      };
    }, {});
    return apiFetch(
      `${ApiBaseUrl}apps/${appId}/paths/set_order?path_type=horizontal,vertical,tile,text,invisible,interstitial,email,bottom_banner`,
      {
        method: "POST",
        body: { paths: prioritizedList },
      },
    );
  }

  static setPriority(appId, paths) {
    const prioritizedList = paths.reduce((h, path) => {
      return {
        ...h,
        [path.id]: { priority: path.priority },
      };
    }, {});
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/set_priority?path_type=custom`, {
      method: "POST",
      body: { paths: prioritizedList },
    });
  }

  static getRetentionModel(
    appId,
    pathType,
    deviceType,
    iab,
    rfLitePathId,
    customDeviceIds = [],
    sequenceId,
  ) {
    const params = new URLSearchParams();
    if (pathType) params.append("path_type", pathType);
    if (deviceType) params.append("device_type", deviceType);
    if (iab) params.append("iab", iab);
    if (rfLitePathId) params.append("rf_lite_path_id", rfLitePathId);
    if (customDeviceIds) params.append("custom_device_ids", customDeviceIds);
    if (sequenceId) params.append("sequence_id", sequenceId);
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/new?${params.toString()}`);
  }

  static retryAndDeleteConnectorError(appId, pathId, connectorError) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/retry_and_delete_connector_error`, {
      method: "POST",
      body: { connector_error: connectorError },
    });
  }

  static deleteConnectorError(appId, pathId, connectorError) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/delete_connector_error`, {
      method: "DELETE",
      body: { connector_error: connectorError },
    });
  }

  static clonePathToApp(currAppId, pathId, targetAppId, defaultCompany, engagementCategory = null) {
    const endpoint = defaultCompany ? "clone_to_app" : "clone_to_company";
    const query = serializeQuery({ clone_app_id: targetAppId });
    const body = engagementCategory ? { engagement_category: engagementCategory } : {};
    return apiFetch(`${ApiBaseUrl}apps/${currAppId}/paths/${pathId}/${endpoint}?${query}`, {
      method: "POST",
      body,
    });
  }

  static clonePath(appId, pathId, pathGroupId, sequenceId, engagementCategory = null) {
    const body = {};
    if (pathGroupId) body.path_group_id = pathGroupId;
    if (sequenceId) body.sequence_id = sequenceId;
    if (engagementCategory) body.engagement_category = engagementCategory;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/clone`, {
      method: "POST",
      body,
    });
  }

  static cloneAsVariation(appId, pathId, targetPathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/clone_as_variation`, {
      method: "POST",
      body: { target_path_id: targetPathId },
    });
  }

  static cloneRfLitePaths(appId, params) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/rf_lite_clone`, {
      method: "POST",
      body: params,
    });
  }

  static resetGoals(appId, pathId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/reset_goals`, {
      method: "POST",
    });
  }

  static updateRecentActivity(appId, pathId, userId, timestamp) {
    const body = {
      recent_activity: {
        user_id: userId,
        timestamp,
      },
    };
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/recent_activity`, {
      method: "PUT",
      body,
    });
  }

  static getRecentPromos(appId, params) {
    let query = serializeQuery(params);
    if (query) query = `&${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/recent_paths?${query}`);
  }

  static sendTestEmail(appId, pathId, recipient, actionGroupId) {
    const params = {
      recipient_email: recipient,
    };
    if (actionGroupId) params.action_group_id = actionGroupId;
    const query = serializeQuery(params);
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/send_test_email?${query}`);
  }

  static sendTestPush(appId, pathId, channel, tokens) {
    const query = serializeQuery({ channel, tokens });
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/send_test_push?${query}`);
  }

  static livePaths(appId, pathIds, language) {
    if (!pathIds || !pathIds.length)
      throw new Error("Expected second parameter to be a non empty array");

    const query = serializeQuery({ path_ids: pathIds.join(), ...(language && { language }) });
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/live?${query}`);
  }

  static getLiveData(appId, pathId, params) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;

    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/live_feed${query}`);
  }

  static bulkUpdate(appId, paths) {
    if (!paths?.length) throw new Error("Expected second parameter to be a non empty array");

    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/bulk_update`, {
      method: "PUT",
      body: { paths },
    });
  }

  static bulkDelete(appId, paths) {
    if (!paths?.length) throw new Error("Expected second parameter to be a non empty array");

    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/bulk_delete`, {
      method: "DELETE",
      body: { paths },
    });
  }

  static bulkTagUpdate(appId, pathIds, tagIdsToAdd = [], tagIdsToRemove = []) {
    if (!pathIds?.length) throw new Error("Expected pathIds to be a non empty array");

    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/bulk_tag_update`, {
      method: "POST",
      body: {
        path_ids: pathIds,
        tag_ids_to_add: tagIdsToAdd,
        tag_ids_to_remove: tagIdsToRemove,
      },
    });
  }

  /**
   * Send a chat message to the AI Design assistant for a prompt.
   *
   * @param {string} appId - The app slug.
   * @param {string} pathId - The path slug.
   * @param {object} params
   * @param {string} params.message - The user's natural language instruction.
   * @param {string} [params.currentCss=""] - The prompt's manual (merchant-authored) custom CSS, sent as context.
   * @param {string} [params.currentAiCss=""] - The previously AI-generated CSS section to merge new changes into.
   * @param {string} [params.pathType] - The prompt's path_type; scopes which selectors the AI targets.
   * @param {Array<{role: string, content: string}>} [params.conversationHistory=[]] - Prior turns.
   * @returns {Promise<{success: boolean, message: string|null, css: string}>}
   */
  static aiDesignChat(
    appId,
    pathId,
    { message, currentCss = "", currentAiCss = "", pathType, conversationHistory = [] },
  ) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/ai_design_chat`, {
      method: "POST",
      body: {
        message,
        current_css: currentCss,
        current_ai_css: currentAiCss,
        path_type: pathType,
        conversation_history: conversationHistory,
      },
    });
  }
}
